import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { HomeHero } from "@/components/home/HomeHero";
import { Marquee } from "@/components/site/Marquee";
import { Reveal, Eyebrow } from "@/components/site/motion";
import { PhotoFrame } from "@/components/site/Placeholders";
import { ServicesBento } from "@/components/site/ServicesBento";
import { PhaseSteps } from "@/components/site/PhaseSteps";
import { Notice112 } from "@/components/site/Notice112";
import { Btn } from "@/components/site/Buttons";
import { CtaBand } from "@/components/site/CtaBand";
import { requestLink } from "@/data/site";

const Intro = () => (
  <section data-testid="home-intro" className="bg-white py-20 md:py-32">
    <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
      <Reveal className="order-2 lg:order-1">
        <PhotoFrame src="/media/ambulanza-nexus.jpg" label="Ambulanza Nexus Italia Group" position="center" className="aspect-[4/3] rounded-[28px] lg:mt-10" testId="home-intro-photo" />
      </Reveal>
      <div className="order-1 lg:order-2">
        <Reveal><Eyebrow>Chi siamo</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 text-3xl font-semibold leading-tight text-navy md:text-[2.6rem]">
            Una rete di professionisti, <span className="text-gold-ink">un unico punto di riferimento.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 text-base leading-relaxed text-slate2 md:text-lg">
            Nexus Italia Group organizza, gestisce e coordina servizi sanitari e sociosanitari. Mettiamo in comunicazione cittadini, strutture sanitarie, aziende e organizzazioni con una rete qualificata di partner operativi.
          </p>
          <p className="mt-4 text-base leading-relaxed text-slate2">
            Non siamo una semplice società di trasporto: integriamo competenze, tecnologia, coordinamento e operatori qualificati, perché ogni servizio sia pianificato con cura.
          </p>
        </Reveal>
        <div className="mt-10 grid gap-px overflow-hidden rounded-2xl border border-navy/10 bg-navy/10 sm:grid-cols-3">
          {[["Organizza", "Ogni richiesta viene pianificata."], ["Coordina", "Il partner giusto, al momento giusto."], ["C'è", "Un riferimento lungo tutto il servizio."]].map(([t, d], i) => (
            <Reveal key={t} delay={0.1 * i} className="bg-white p-6">
              <p className="text-xs font-semibold tracking-[0.2em] text-gold-ink">NEXUS</p>
              <p className="mt-2 text-xl font-semibold text-navy">{t}</p>
              <p className="mt-2 text-sm text-slate2">{d}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="mt-10">
          <Link to="/chi-siamo" data-testid="home-intro-about-link" className="group inline-flex items-center gap-2 text-sm font-semibold text-navy">
            Conosci Nexus <ArrowUpRight className="h-4 w-4 text-gold transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const Services = () => (
  <section data-testid="home-services" className="bg-mist py-20 md:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="mb-12 flex flex-col gap-6 md:mb-16 md:flex-row md:items-end md:justify-between">
        <div>
          <Reveal><Eyebrow>I nostri servizi</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 max-w-2xl text-3xl font-semibold leading-tight text-navy md:text-[2.6rem]">Sei aree di servizio, un'unica organizzazione.</h2>
          </Reveal>
        </div>
        <Reveal delay={0.2}><Btn to="/servizi" variant="ghost" testId="home-all-services-btn">Tutti i servizi</Btn></Reveal>
      </div>
      <ServicesBento />
    </div>
  </section>
);

const Centrale = () => (
  <section data-testid="home-centrale" className="grain relative overflow-hidden bg-navy py-20 text-white md:py-32">
    <div className="pointer-events-none absolute -left-40 top-20 h-[520px] w-[520px] rounded-full border border-gold/20" />
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] lg:items-end">
        <div>
          <Reveal><Eyebrow light>Centrale Operativa</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 text-3xl font-semibold leading-tight md:text-[2.6rem]">Il cuore organizzativo di Nexus.</h2>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <p className="text-base leading-relaxed text-white/80">
            La Centrale Operativa riceve le richieste, pianifica, individua il partner più adatto e coordina il servizio. Operatori dedicati, supportati da strumenti digitali, con reperibilità H24 secondo le modalità di servizio previste.
          </p>
        </Reveal>
      </div>
      <div className="mt-16 md:mt-20"><PhaseSteps light /></div>
      <Reveal className="mt-14 flex flex-col gap-4 sm:flex-row">
        <Btn to="/centrale-operativa" variant="gold" testId="home-centrale-btn">Scopri la Centrale Operativa</Btn>
      </Reveal>
      <Reveal className="mt-14"><Notice112 /></Reveal>
    </div>
  </section>
);

const ADV = [
  {
    t: "Per i privati",
    d: "Per chi ha bisogno di un trasporto o di un servizio per sé o per un familiare.",
    items: ["Un solo contatto per organizzare il servizio", "Il mezzo e il partner più adatti alla persona", "Informazioni chiare prima e durante il servizio", "Attenzione, rispetto e ascolto"],
    to: requestLink("trasporto"),
    cta: "Richiedi un servizio",
  },
  {
    t: "Per le strutture sanitarie",
    d: "Per RSA, ospedali, cliniche, centri medici e diagnostici, strutture riabilitative e comunità.",
    items: ["Un unico interlocutore per tutte le richieste", "Gestione centralizzata tramite la Centrale Operativa", "Trasporti programmati, ricorrenti ed estemporanei", "Accordi e convenzioni personalizzate"],
    to: requestLink("convenzione"),
    cta: "Richiedi una convenzione",
  },
];

const Advantages = () => (
  <section data-testid="home-advantages" className="bg-white py-20 md:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal><Eyebrow>I vantaggi</Eyebrow></Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-6 max-w-3xl text-3xl font-semibold leading-tight text-navy md:text-[2.6rem]">Ogni richiesta merita attenzione, competenza e una risposta organizzata.</h2>
      </Reveal>
      <div className="mt-14 grid gap-6 lg:grid-cols-2">
        {ADV.map((a, i) => (
          <Reveal key={a.t} delay={i * 0.12} className="group relative flex flex-col rounded-[24px] border border-navy/10 bg-mist p-8 transition-[border-color] duration-500 hover:border-gold/60 md:p-12">
            <span className="absolute left-8 top-0 h-1 w-16 bg-gold md:left-12" />
            <h3 className="text-2xl font-semibold text-navy">{a.t}</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate2">{a.d}</p>
            <ul className="mt-8 space-y-4">
              {a.items.map((it) => (
                <li key={it} className="flex items-start gap-3 text-base text-navy">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-gold/60"><Check className="h-3.5 w-3.5 text-gold-ink" /></span>
                  {it}
                </li>
              ))}
            </ul>
            <div className="mt-10"><Btn to={a.to} variant={i ? "primary" : "ghost"} testId={`home-advantage-btn-${i}`}>{a.cta}</Btn></div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Partners = () => (
  <section data-testid="home-partners" className="bg-mist py-20 md:py-32">
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
      <div>
        <Reveal><Eyebrow>Rete di partner</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 text-3xl font-semibold leading-tight text-navy md:text-[2.6rem]">Le competenze di ciascuno. <span className="text-gold-ink">La forza di una rete.</span></h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 text-base leading-relaxed text-slate2 md:text-lg">
            Nexus collabora con associazioni, cooperative, imprese e organizzazioni del settore. La Centrale Operativa coordina le attività e mette in contatto le richieste con il partner più adatto per competenze, mezzi e territorio.
          </p>
        </Reveal>
        <Reveal delay={0.3} className="mt-10 flex flex-col gap-4 sm:flex-row">
          <Btn to="/diventa-partner" testId="home-partner-btn">Entra nella rete Nexus</Btn>
        </Reveal>
      </div>
      <Reveal delay={0.1}>
        <PhotoFrame src="/media/terzo-settore.jpg" label="Terzo settore: la rete dei partner Nexus" className="min-h-[320px] rounded-[28px] md:min-h-[460px]" testId="home-partners-photo" />
      </Reveal>
    </div>
  </section>
);

const Training = () => (
  <section data-testid="home-training" className="bg-white py-20 md:py-28">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="grid overflow-hidden rounded-[28px] border border-navy/10 lg:grid-cols-[1fr_1.2fr]">
        <PhotoFrame src="/media/formazione-rcp.jpg" position="center top" label="Formazione Nexus: manovre di rianimazione cardiopolmonare" className="min-h-[340px] sm:min-h-[420px] lg:min-h-full" testId="home-training-photo" />
        <div className="bg-white p-8 md:p-14">
          <Eyebrow>Formazione</Eyebrow>
          <h2 className="mt-6 text-3xl font-semibold leading-tight text-navy md:text-4xl">BLSD e primo soccorso.</h2>
          <p className="mt-6 text-base leading-relaxed text-slate2 md:text-lg">
            La conoscenza può fare la differenza. Imparare a intervenire significa essere pronti quando conta davvero.
          </p>
          <p className="mt-4 text-sm leading-relaxed text-slate2">
            Corsi per operatori laici e sanitari, uso del DAE, disostruzione delle vie aeree e manovre salvavita pediatriche, con formatori qualificati nel rispetto dei requisiti applicabili.
          </p>
          <div className="mt-10"><Btn to="/servizi/formazione-blsd-primo-soccorso" testId="home-training-btn">Scopri i nostri corsi</Btn></div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <HomeHero />
      <Marquee />
      <Intro />
      <Services />
      <Centrale />
      <Advantages />
      <Partners />
      <Training />
      <CtaBand />
    </div>
  );
}
